/**
 * 벽 구간 채우기 규칙 — "왼쪽부터 900, 나머지 오른쪽" 같은 규칙으로 구간 패널 사이즈를 결정
 *
 * 구간 길이 total = Σ패널 + 패널 수 × 간격 (분할 규칙: 접합부 n×4)
 * 순서는 배치 순서 기준 (가로벽: 왼쪽→오른쪽, 세로벽: 위→아래)
 */

export type RestPos = 'end' | 'start' | 'center' | 'split';

export interface SegRule {
  base: number;                    // 채우는 기준 크기 (예: 900)
  rest: RestPos;                   // 나머지 위치: 끝 / 시작 / 가운데 / 양끝 나눔
  locks: Record<number, number>;   // 고정: 구간 내 순번(1부터) → 크기
}

export interface FillSpec {
  min: number;      // 최소 패널 (200)
  max: number;      // 최대 패널 = 표준 폭 (900)
  standard: number; // 표준 폭
  half: number;     // 반절 (441)
  space: number;    // 접합 간격 (4)
}

export const defaultRule = (spec: FillSpec): SegRule => ({ base: spec.standard, rest: 'end', locks: {} });

export function fillSegment(total: number, rule: SegRule, spec: FillSpec): { sizes: number[]; error: string | null } {
  const { min, max, standard, half, space: sp } = spec;
  if (rule.base < min || rule.base > max) return { sizes: [], error: `기준 크기는 ${min}~${max}mm` };
  const base = rule.base;
  const lockCount = Object.keys(rule.locks).length;
  // 한 장으로 충분한 구간: 한 장 + 간격 4 (엔진 분할 규칙과 동일)
  if (total <= max + sp && lockCount === 0) return total - sp >= min ? { sizes: [total - sp], error: null } : { sizes: [], error: `구간이 너무 짧음 (${total}mm)` };

  // 1) 기준 크기로 채우고, 마지막 조각이 최소 이상 남도록 멈춤
  const full: number[] = [];
  let rem = total;
  while (rem - (base + sp) >= min + sp) { full.push(base); rem -= base + sp; }
  const last = rem - sp;
  if (last < min) return { sizes: [], error: `구간이 너무 짧음 (${total}mm)` };
  // 마지막 조각이 최대를 넘으면 둘로 나눔 (표준 폭이면 반절 전략: 441 + 나머지)
  let custom: number[];
  if (last <= max) custom = [last];
  else if (base === standard) custom = [half, last - half - sp];
  else { const a = Math.floor((last - sp) / 2); custom = [a, last - sp - a]; }

  // 2) 나머지 위치 배치
  let sizes: number[];
  let restIdx: number[]; // 나머지(흡수 후보) 위치
  if (rule.rest === 'end') {
    sizes = [...full, ...custom];
    restIdx = custom.map((_, i) => full.length + i);
  } else if (rule.rest === 'start') {
    sizes = [...[...custom].reverse(), ...full];
    restIdx = custom.map((_, i) => i);
  } else if (rule.rest === 'center') {
    const mid = Math.floor(full.length / 2);
    sizes = [...full.slice(0, mid), ...custom, ...full.slice(mid)];
    restIdx = custom.map((_, i) => mid + i);
  } else {
    // 양끝 나눔: 나머지 조각들을 두 장으로 합쳐 양 끝에 배치 (간격 수 보정)
    const R = custom.reduce((a, v) => a + v, 0) + sp * (custom.length - 2);
    const a = Math.floor(R / 2), b = R - a;
    if (a < min) return { sizes: [], error: `양끝 나머지가 ${a}mm로 너무 작음 (최소 ${min})` };
    if (b > max) return { sizes: [], error: `양끝 나머지가 ${b}mm로 너무 큼 (최대 ${max})` };
    sizes = [a, ...full, b];
    restIdx = [0, sizes.length - 1];
  }

  // 3) 고정 적용 → 나머지 조각이 차이 흡수
  const lockIdx = Object.keys(rule.locks).map(k => Number(k) - 1);
  for (const i of lockIdx) {
    if (i < 0 || i >= sizes.length) return { sizes: [], error: `${i + 1}번 패널 없음 (이 구간은 ${sizes.length}장)` };
    sizes[i] = rule.locks[i + 1];
  }
  if (lockIdx.length > 0) {
    let free = [...restIdx].reverse().find(i => !lockIdx.includes(i))
      ?? sizes.map((_, i) => i).reverse().find(i => !lockIdx.includes(i));
    // 메울 패널이 없으면(전부 고정) 끝에 패널을 하나 추가해 나머지를 담음
    if (free === undefined) { sizes.push(0); free = sizes.length - 1; }
    const others = sizes.reduce((acc, v, i) => (i === free ? acc : acc + v), 0);
    sizes[free] = total - sp * sizes.length - others;
    // 메우는 패널이 최대를 넘으면 둘로 나눔 (최대 + 나머지, 나머지가 최소 미만이면 반절 + 나머지)
    const v = sizes[free];
    if (v > max) {
      const r1 = v - max - sp, r2 = v - half - sp;
      const pair = r1 >= min ? [max, r1] : r2 >= min && r2 <= max ? [half, r2] : [Math.floor((v - sp) / 2), v - sp - Math.floor((v - sp) / 2)];
      sizes.splice(free, 1, ...pair);
    }
  }

  const bad = sizes.findIndex(v => v < min || v > max);
  if (bad >= 0) return { sizes, error: `${bad + 1}번 패널이 ${sizes[bad]}mm가 됨 (허용 ${min}~${max})` };
  return { sizes, error: null };
}

export type ParseResult = { rule?: SegRule; reset?: boolean; error?: string };

/** 한 줄 명령 해석 — 지원 표현은 COMMAND_EXAMPLES 참고 */
export function parseCommand(text: string, current: SegRule): ParseResult {
  let t = text.replace(/\s+/g, ' ').trim();
  if (!t) return { error: '명령을 입력하세요' };
  if (/초기화|원래대로|기본값|리셋/.test(t)) return { reset: true };

  const rule: SegRule = { ...current, locks: { ...current.locks } };
  let matched = false;

  if (/고정\s*(해제|풀|취소|없)/.test(t)) { rule.locks = {}; matched = true; t = t.replace(/고정\s*(해제|풀|취소|없)\S*/g, ' '); }
  // "2번 600 고정", "3번 패널을 500으로"
  t = t.replace(/(\d+)\s*번(?:\s*패널)?\s*(?:을|를|은|는|이|가)?\s*(\d{3,4})\s*(?:mm)?\s*(?:으로|로)?\s*(?:고정)?/g, (_, i, v) => {
    rule.locks[Number(i)] = Number(v); matched = true; return ' ';
  });

  // 나머지 위치 — "…부터"가 가장 명확, 그다음 "나머지 …"
  const A = '(왼쪽|위|처음|앞|시작)', B = '(오른쪽|아래|끝|뒤|마지막)';
  if (/가운데|중앙|중간/.test(t)) { rule.rest = 'center'; matched = true; }
  else if (/양끝|양 끝|양쪽/.test(t)) { rule.rest = 'split'; matched = true; }
  else if (new RegExp(`${B}\\s*(에서)?\\s*부터`).test(t)) { rule.rest = 'start'; matched = true; }
  else if (new RegExp(`${A}\\s*(에서)?\\s*부터`).test(t)) { rule.rest = 'end'; matched = true; }
  else if (new RegExp(`나머지\\S*\\s*${B}`).test(t)) { rule.rest = 'end'; matched = true; }
  else if (new RegExp(`나머지\\S*\\s*${A}`).test(t)) { rule.rest = 'start'; matched = true; }

  // 기준 크기: 남은 숫자 중 첫 번째 (예: "900으로", "441 기준")
  const m = t.match(/(\d{3,4})\s*(?:mm)?/);
  if (m) { rule.base = Number(m[1]); matched = true; }

  if (!matched) return { error: '이해하지 못했어요. 예: "왼쪽부터 900, 나머지 오른쪽"' };
  return { rule };
}

/** 규칙 → 명령 문장 (버튼 조작 시 명령창에 표시) */
export function ruleToText(rule: SegRule, orient: 'h' | 'v'): string {
  const [a, b] = orient === 'h' ? ['왼쪽', '오른쪽'] : ['위', '아래'];
  const head = rule.rest === 'end' ? `${a}부터 ${rule.base}, 나머지 ${b}`
    : rule.rest === 'start' ? `${b}부터 ${rule.base}, 나머지 ${a}`
    : rule.rest === 'center' ? `${rule.base} 기준, 나머지 가운데`
    : `${rule.base} 기준, 나머지 양끝`;
  const locks = Object.entries(rule.locks).map(([i, v]) => `${i}번 ${v} 고정`);
  return [head, ...locks].join(', ');
}

export const COMMAND_EXAMPLES = [
  '왼쪽부터 900, 나머지 오른쪽',
  '오른쪽부터 900',
  '900 기준, 나머지 가운데',
  '나머지 양끝',
  '441 기준',
  '2번 600 고정',
  '고정 해제',
  '초기화',
];
